import { get } from "lodash";
import { useState } from "react";
import toast from "react-hot-toast";

import { Badge } from "@shared/ui";

import type { ClientConfig, VerificationConfig } from "@platform/dmv-api";

import { updateClientsConfig } from "../../lib/automatic";
import SettingsSection from "./SettingsSection";
import VerificationModal, {
  DEFAULT_VERIFICATION_MESSAGE,
  type VerificationChannel,
  type VerificationChannelConfig,
} from "./VerificationModal";

const CHANNELS: {
  channel: VerificationChannel;
  label: string;
  field: "subject" | "sender";
}[] = [
  { channel: "email", label: "Email", field: "subject" },
  { channel: "sms", label: "SMS", field: "sender" },
];

const VerificationSection = ({
  id,
  config,
  clientId,
}: {
  id?: string;
  config: ClientConfig | null;
  clientId: string;
}) => {
  const [verification, setVerification] = useState<VerificationConfig>(
    get(config, "verificationConfig", {})
  );
  const [editingChannel, setEditingChannel] =
    useState<VerificationChannel | null>(null);

  const saveChannel = async (
    channel: VerificationChannel,
    channelConfig: VerificationChannelConfig,
    successMessage: string
  ) => {
    const loadingToast = toast.loading("Saving...");
    const [, error] = await updateClientsConfig(clientId, {
      verificationConfig: { [channel]: channelConfig },
    });
    toast.dismiss(loadingToast);
    if (error) {
      toast.error("Oops! Something went wrong. Please try again.");
      return;
    }
    toast.success(successMessage);
    setVerification((prev) => ({ ...prev, [channel]: channelConfig }));
  };

  // Delete disables the channel and resets its content; subject/sender stay
  // because DMV requires them.
  const handleDelete = (channel: VerificationChannel) =>
    saveChannel(
      channel,
      {
        ...verification[channel]!,
        enabled: false,
        message: DEFAULT_VERIFICATION_MESSAGE,
        ...(verification[channel]!.reminders && {
          reminders: { enabled: false, daysAfter: [] },
        }),
      } as VerificationChannelConfig,
      "Verification message deleted"
    );

  const handleSave = (channelConfig: VerificationChannelConfig) =>
    saveChannel(
      editingChannel!,
      channelConfig,
      verification[editingChannel!]
        ? "Verification message updated"
        : "Verification message added"
    );

  return (
    <>
      <SettingsSection
        id={id}
        title="Verification"
        subtitle={
          <>
            Configure the email and SMS messages sent to end users, inviting
            them to verify their insurance information.
            <details className="mt-2 text-xs text-gray-500">
              <summary className="cursor-pointer select-none font-medium text-gray-600">
                Customize message with dynamic fields
              </summary>
              <p className="mt-1">
                Text wrapped in {"{{ }}"} is replaced with recipient-specific
                details when the message is sent. You can use:
              </p>
              <ul className="mt-1 list-disc pl-4">
                <li>
                  <code className="font-mono">{"{{firstName}}"}</code> — the
                  recipient's first name
                </li>
                <li>
                  <code className="font-mono">{"{{clientName}}"}</code> — your
                  company name
                </li>
                <li>
                  <code className="font-mono">{"{{ignitionUri}}"}</code> — the
                  recipient's personalized verification link (SMS only)
                </li>
              </ul>
            </details>
          </>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="pb-3 pr-6 font-medium text-gray-700">Channel</th>
                <th className="pb-3 pr-6 font-medium text-gray-700">Status</th>
                <th className="pb-3 pr-6 font-medium text-gray-700">
                  Subject / Sender
                </th>
                <th className="pb-3 pr-6 font-medium text-gray-700">Content</th>
                <th className="pb-3 font-medium text-gray-700 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {CHANNELS.map(({ channel, label, field }) => {
                const channelConfig = verification[channel];
                return (
                  <tr
                    key={channel}
                    className="hover:bg-gray-50 group align-top"
                  >
                    <td className="py-3 pr-6">{label}</td>
                    <td className="py-3 pr-6">
                      {channelConfig ? (
                        <Badge
                          status={channelConfig.enabled ? "active" : "inactive"}
                        />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-6">
                      {channelConfig ? (
                        get(channelConfig, field)
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-6">
                      {channelConfig ? (
                        <code className="text-xs text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded break-all font-mono w-fit">
                          {channelConfig.message}
                        </code>
                      ) : (
                        <span className="text-gray-400">Not configured</span>
                      )}
                    </td>
                    <td className="py-3 align-middle">
                      {channelConfig ? (
                        <div className="flex gap-x-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setEditingChannel(channel)}
                            className="text-sm text-gray-500 hover:text-black"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(channel)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingChannel(channel)}
                          className="text-sm text-gray-500 hover:text-black"
                        >
                          Add
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SettingsSection>

      {editingChannel && (
        <VerificationModal
          isOpen
          onClose={() => setEditingChannel(null)}
          onSave={handleSave}
          channel={editingChannel}
          initialData={verification[editingChannel]}
        />
      )}
    </>
  );
};

export default VerificationSection;
